import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import {
  AddCircle,
  AddCircleTwoTone,
  Search,
  Favorite,
  FavoriteBorder,
} from "@mui/icons-material";
import { newTask, tasks } from "../../Static/task";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function AddTaskButton({
  onAdd,
  open: controlledOpen,
  onClose: controlledOnClose,
  ...props
}) {
  const [localOpen, setLocalOpen] = React.useState(false);
  const theme = useTheme();

  // Check if open state is controlled externally
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : localOpen;

  // When component first mounts or controlledOpen changes, sync state if needed
  React.useEffect(() => {
    if (isControlled && controlledOpen !== localOpen) {
      setLocalOpen(controlledOpen);
    }
  }, [controlledOpen, isControlled, localOpen]);

  const handleOpen = () => {
    // For touch devices, add haptic feedback
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(50); // Short vibration when adding action
    }

    if (!isControlled) {
      setLocalOpen(true);
    }
  };

  const onClose = () => {
    if (!isControlled) {
      setLocalOpen(false);
    } else if (controlledOnClose) {
      controlledOnClose();
    }
  };

  return (
    <>
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{
            minWidth: 220,
            p: 2,
            fontSize: 24,
            borderRadius: 3,
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
            boxShadow: "0 8px 16px rgba(25, 118, 210, 0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-3px)",
              boxShadow: "0 12px 20px rgba(25, 118, 210, 0.4)",
            },
            "&:active": {
              transform: "translateY(1px)",
            },
          }}
          onClick={handleOpen}
          {...props}
          startIcon={<AddCircle sx={{ fontSize: 28 }} />}
        >
          Add New Action
        </Button>
      </Box>

      <AddTaskDialog open={isOpen} onClose={onClose} onAdd={onAdd} />
    </>
  );
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

function AddTaskDialog({ open, onClose, onAdd }) {
  const theme = useTheme();
  const [t, setT] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [expanded, setExpanded] = React.useState(false);

  // Categorize tasks by type
  const categorizedTasks = categorizeTasks(tasks);

  // Filter tasks based on search
  const filteredCategories = React.useMemo(() => {
    if (!search) return categorizedTasks;

    const result = {};
    Object.keys(categorizedTasks).forEach((category) => {
      const filtered = categorizedTasks[category].filter((task) =>
        task.name.toLowerCase().includes(search.toLowerCase())
      );
      if (filtered.length > 0) {
        result[category] = filtered;
      }
    });
    return result;
  }, [categorizedTasks, search]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          background: theme.palette.background.default,
          backdropFilter: "blur(15px)",
          border: `1px solid ${theme.palette.primary.main}`,
          borderRadius: 3,
          boxShadow: `0 0 20px rgba(${theme.palette.primary.main}, 0.3)`,
          padding: 1,
          overflow: "hidden",
        },
      }}
      maxWidth="md"
      fullWidth
      onTransitionExited={() => {
        if (t) {
          setT(null);
          onAdd({ ...t, id: Date.now().toString() });
        }
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: "relative",
          width: "100%",
          zIndex: 2,
          backgroundColor: "transparent",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          p: 1,
          px: 2,
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.4rem",
            color: theme.palette.primary.light,
            fontWeight: "600",
            p: 1,
          }}
        >
          Select an Action
        </DialogTitle>
        <Tooltip title="Close" arrow>
          <IconButton
            color="error"
            onClick={onClose}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                transform: "rotate(90deg)",
                transition: "all 0.3s ease",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search actions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="primary" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              color: theme.palette.text.primary,
            },
          }}
          sx={{ mb: 3 }}
        />

        <Box
          sx={{
            height: "450px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pr: 1,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          {Object.entries(filteredCategories).length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                opacity: 0.6,
              }}
            >
              <Typography
                variant="h6"
                color={theme.palette.text.secondary}
                gutterBottom
              >
                No actions found
              </Typography>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Try a different search term
              </Typography>
            </Box>
          )}

          {Object.keys(filteredCategories)
            .sort()
            .map((category, idx) => (
              <Accordion
                key={idx}
                expanded={expanded === `panel-${idx}`}
                onChange={handleAccordionChange(`panel-${idx}`)}
                sx={{
                  background: theme.palette.background.default,
                  backdropFilter: "blur(10px)",
                  borderRadius: "12px !important",
                  mb: 2,
                  "&::before": {
                    display: "none",
                  },
                  boxShadow:
                    expanded === `panel-${idx}`
                      ? `0 0 15px rgba(${getCategoryColor(
                          category,
                          theme
                        )}, 0.3)`
                      : "none",
                  transition: "all 0.3s ease",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: getCategoryColorString(category, theme),
                        transition: "transform 0.3s ease",
                        transform:
                          expanded === `panel-${idx}`
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                      }}
                    />
                  }
                  aria-controls={`panel${idx}-content`}
                  id={`panel${idx}-header`}
                  sx={{
                    borderLeft: `4px solid ${getCategoryColorString(
                      category,
                      theme
                    )}`,
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: theme.palette.background.default,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: getCategoryColorString(category, theme),
                      fontWeight: 600,
                    }}
                  >
                    {category} ({filteredCategories[category].length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    {filteredCategories[category].map((task, i) => (
                      <Zoom
                        in
                        key={i}
                        style={{ transitionDelay: `${i * 50}ms` }}
                      >
                        <Box>
                          <TaskSelectButton
                            task={task}
                            categoryColor={getCategoryColorString(
                              category,
                              theme
                            )}
                            onAdd={() => {
                              setT(task);
                              onClose();
                            }}
                          />
                        </Box>
                      </Zoom>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function TaskSelectButton({ task, categoryColor, onAdd }) {
  const theme = useTheme();
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Button
      size="large"
      variant="outlined"
      color="inherit"
      sx={{
        width: "100%",
        justifyContent: "flex-start",
        p: 2,
        borderRadius: "12px",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        borderColor: "rgba(255, 255, 255, 0.1)",
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          transform: "translateY(-3px)",
          boxShadow: `0 5px 15px rgba(0, 0, 0, 0.2), 0 0 5px ${categoryColor}`,
          borderColor: categoryColor,
        },
        "&:after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: isHovering
            ? `linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)`
            : "none",
          transform: isHovering ? "translateX(100%)" : "translateX(-100%)",
          transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
      onClick={onAdd}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      startIcon={
        <Avatar
          sx={{
            bgcolor: `${categoryColor}33`,
            width: 40,
            height: 40,
            mr: 1,
            boxShadow: `0 0 8px ${categoryColor}80`,
            transition: "all 0.3s ease",
            transform: isHovering ? "scale(1.1)" : "scale(1)",
          }}
        >
          <AddCircleTwoTone sx={{ color: categoryColor }} />
        </Avatar>
      }
    >
      <Box sx={{ textAlign: "left" }}>
        <Typography
          variant="subtitle1"
          fontWeight="600"
          color={theme.palette.text.primary}
        >
          {task.name}
        </Typography>
        {task.description && (
          <Typography
            variant="caption"
            color={theme.palette.text.secondary}
            sx={{ display: "block", mt: 0.5 }}
          >
            {task.description}
          </Typography>
        )}
      </Box>
    </Button>
  );
}

// Helper function to get category color
function getCategoryColorString(category, theme) {
  // const colors = {
  //   "Email": theme.palette.info.main,
  //   "Message": theme.palette.success.main,
  //   "Call": theme.palette.warning.main,
  //   "Meeting": theme.palette.error.main,
  //   "Other Action": theme.palette.primary.main
  // };
  const colors = {
    "Dispenser Action": theme.palette.info.main,
    "Nutripot Action": theme.palette.success.main,
    "Pour Action": theme.palette.warning.main,
    "Stir Action": theme.palette.error.main,
    "Waiting Action": theme.palette.secondary.main,
    "Chimney Action": theme.palette.primary.main,
  };
  return colors[category] || theme.palette.primary.main;
}

function getCategoryColor(category, theme) {
  const colorString = getCategoryColorString(category, theme);
  // Extract RGB values from the color string
  const rgbMatch = colorString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (rgbMatch) {
    return `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}`;
  }
  return "25, 118, 210"; // Default primary color
}

const categorizeTasks = (tasks) => {
  return tasks.reduce((categories, task) => {
    const category = task.category || "Other Action";
    if (!categories[category]) {
      categories[category] = [];
    }
    categories[category].push(task);
    return categories;
  }, {});
};
